import { LazyMotion } from 'framer-motion'
import { useLayoutEffect } from 'react'
import { OverlayProvider } from 'react-aria'

import { SpriteIconsContext } from '@ui/Icon/SpriteIconsContext'
import SensorProvider from '@ui/SensorProvider'
import TooltipProvider from '@ui/TooltipProvider'
import {
  type ThemeKey,
  ThemeProvider,
  type ThemeVariant,
  type TypographyThemeKey,
  useTheme,
} from '@ui/theme'

export interface DesignSystemProviderProps {
  children?: React.ReactNode
  spriteUrl?: string
  themeKey?: ThemeKey
  typographyThemeKey?: TypographyThemeKey
  themeVariant?: ThemeVariant
}

const loadFeatures = async () =>
  (await import('@ui/animations/FramerLazyFeatures')).default

const DesignSystemProvider = ({
  children,
  spriteUrl = '',
  themeKey = 'light',
  typographyThemeKey = 'default',
  themeVariant = 'default',
}: DesignSystemProviderProps) => {
  return (
    <ThemeProvider
      themeKey={themeKey}
      typographyThemeKey={typographyThemeKey}
      themeVariant={themeVariant}
    >
      <GlobalThemeUpdater />
      <SpriteIconsContext.Provider value={{ spriteUrl }}>
        <SensorProvider>
          <OverlayProvider style={{ flex: 1, height: '100%' }}>
            <TooltipProvider>
              <LazyMotion strict features={loadFeatures}>
                {children}
              </LazyMotion>
            </TooltipProvider>
          </OverlayProvider>
        </SensorProvider>
      </SpriteIconsContext.Provider>
    </ThemeProvider>
  )
}

export default DesignSystemProvider

function GlobalThemeUpdater() {
  const { themeClassName } = useTheme()

  useLayoutEffect(() => {
    const tokens = themeClassName.split(' ')
    document.body.classList.add(...tokens)

    return () => {
      document.body.classList.remove(...tokens)
    }
  }, [themeClassName])

  return null
}
